import { useRef, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download, Paperclip, Pencil, Trash2 } from 'lucide-react';
import { api, apiDownload, apiUpload, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useT } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import type { AttachmentKind, InquiryAttachment } from '../../types/api';
import { Button } from '../Button';
import { Input } from '../Input';
import { Modal } from '../Modal';
import { Select } from '../Select';
import { Skeleton } from '../Skeleton';
import { TextArea } from '../TextArea';
import { useToast } from '../Toast';
import styles from './AttachmentsSection.module.css';

export interface AttachmentsSectionProps {
  inquiryId: string;
  /** Issue #30 — integrators only download CONCEPT/TKP artifacts; no upload UI. */
  canUpload?: boolean;
}

const KIND_LABEL_KEY: Record<AttachmentKind, TranslationKey> = {
  CONCEPT: 'attachments.kindConcept',
  TKP: 'attachments.kindTkp',
  REPORT: 'attachments.kindReport',
  OTHER: 'attachments.kindOther',
};

const MAX_SIZE = 15 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** True when the drag carries OS files (not e.g. selected text). */
function hasDraggedFiles(e: DragEvent): boolean {
  return e.dataTransfer.types.includes('Files');
}

/**
 * Issue #27 — artifacts of an inquiry (concept / TKP / report).
 * Issue #43 — compact: the always-expanded upload form collapsed into a
 * button + modal (title / description / kind / file); list rows stay one line.
 */
export function AttachmentsSection({ inquiryId, canUpload = true }: AttachmentsSectionProps) {
  const { t, locale } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InquiryAttachment | null>(null);
  const [deleting, setDeleting] = useState<InquiryAttachment | null>(null);
  const [kind, setKind] = useState<AttachmentKind>('CONCEPT');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Nested children fire dragenter/dragleave pairs — track the depth so the
  // highlight survives moving across them.
  const dragDepth = useRef(0);

  const attachmentsQ = useQuery<InquiryAttachment[]>({
    queryKey: ['inquiries', inquiryId, 'attachments'],
    queryFn: () => api.get<InquiryAttachment[]>(`/inquiries/${inquiryId}/attachments`),
  });

  const resetForm = () => {
    setKind('CONCEPT');
    setTitle('');
    setDescription('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadMutation = useMutation({
    mutationFn: (input: { file: File; kind: AttachmentKind; title: string; description: string }) => {
      const form = new FormData();
      form.append('kind', input.kind);
      if (input.title.trim()) form.append('title', input.title.trim());
      if (input.description.trim()) form.append('description', input.description.trim());
      form.append('file', input.file);
      return apiUpload<InquiryAttachment>(`/inquiries/${inquiryId}/attachments`, form);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries', inquiryId, 'attachments'] });
      // Upload emits an ATTACH timeline event on the server
      void queryClient.invalidateQueries({ queryKey: ['inquiries', inquiryId, 'transitions'] });
      toast.success(t('attachments.toastUploaded'));
      setModalOpen(false);
      resetForm();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('attachments.toastFailed'), msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; kind: AttachmentKind; title: string; description: string }) =>
      api.patch<InquiryAttachment>(`/inquiries/${inquiryId}/attachments/${input.id}`, {
        kind: input.kind,
        title: input.title.trim(),
        description: input.description.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries', inquiryId, 'attachments'] });
      toast.success(t('attachments.toastUpdated'));
      setEditing(null);
      resetForm();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('attachments.toastUpdateFailed'), msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/inquiries/${inquiryId}/attachments/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries', inquiryId, 'attachments'] });
      toast.success(t('attachments.toastDeleted'));
      setDeleting(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('attachments.toastDeleteFailed'), msg);
    },
  });

  const submit = () => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error(t('attachments.toastFailed'), t('attachments.tooBig'));
      return;
    }
    uploadMutation.mutate({ file, kind, title, description });
  };

  // Edit reuses the same modal without the file field — the bytes stay as-is.
  const openEdit = (att: InquiryAttachment) => {
    resetForm();
    setKind(att.kind);
    setTitle(att.title ?? '');
    setDescription(att.description ?? '');
    setEditing(att);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    resetForm();
  };

  const submitEdit = () => {
    if (!editing) return;
    updateMutation.mutate({ id: editing.id, kind, title, description });
  };

  const formBusy = uploadMutation.isPending || updateMutation.isPending;
  const anyModalOpen = modalOpen || editing !== null;

  // Drag & drop is an enhancement only — the button + modal stays the
  // primary path. Drop opens the same modal with the file pre-filled.
  const onSectionDragEnter = (e: DragEvent) => {
    if (!canUpload || !hasDraggedFiles(e)) return;
    e.preventDefault();
    if (anyModalOpen) return; // the modal's file field handles its own drop
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onSectionDragOver = (e: DragEvent) => {
    if (!canUpload || !hasDraggedFiles(e)) return;
    e.preventDefault(); // allow the drop, keep the browser from opening the file
  };
  const onSectionDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onSectionDrop = (e: DragEvent) => {
    if (!canUpload || !hasDraggedFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (anyModalOpen) return;
    const dropped = e.dataTransfer.files[0];
    if (!dropped) return;
    if (dropped.size > MAX_SIZE) {
      toast.error(t('attachments.toastFailed'), t('attachments.tooBig'));
      return;
    }
    resetForm();
    setFile(dropped);
    setModalOpen(true);
  };
  // The modal is portaled, but React events still bubble to the section —
  // stopPropagation keeps a field drop from re-triggering the section drop.
  const onFileFieldDragOver = (e: DragEvent) => {
    if (hasDraggedFiles(e)) e.preventDefault();
  };
  const onFileFieldDrop = (e: DragEvent) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  const download = async (attachment: InquiryAttachment) => {
    try {
      const { blob, filename } = await apiDownload(
        `/inquiries/${inquiryId}/attachments/${attachment.id}/download`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('attachments.toastFailed'), msg);
    }
  };

  return (
    <section
      aria-label={t('attachments.title')}
      className={clsx(styles.section, dragOver && styles.dragOver)}
      onDragEnter={onSectionDragEnter}
      onDragOver={onSectionDragOver}
      onDragLeave={onSectionDragLeave}
      onDrop={onSectionDrop}
    >
      <div className={styles.head}>
        <h3 className={styles.title}>{t('attachments.title')}</h3>
        {canUpload && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Paperclip size={14} />}
            onClick={() => setModalOpen(true)}
          >
            {t('attachments.uploadArtifact')}
          </Button>
        )}
      </div>

      {attachmentsQ.isLoading ? (
        <div className={styles.skeleton}>
          <Skeleton width="60%" />
          <Skeleton width="45%" />
        </div>
      ) : (attachmentsQ.data ?? []).length === 0 ? (
        <p className={styles.empty}>
          {t('attachments.empty')}
          {canUpload && <span className={styles.dropHint}>{t('attachments.dropHint')}</span>}
        </p>
      ) : (
        <ul className={styles.list}>
          {(attachmentsQ.data ?? []).map((att) => (
            <li key={att.id} className={styles.item} title={att.description ?? undefined}>
              <div className={styles.itemMain}>
                <span className={styles.filename}>{att.title || att.filename}</span>
                <span className={styles.meta}>
                  {t(KIND_LABEL_KEY[att.kind])} · {formatBytes(att.size)} ·{' '}
                  {formatDateTime(att.createdAt, locale)}
                  {att.uploadedBy?.name ? ` · ${att.uploadedBy.name}` : ''}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={t('attachments.download')}
                leftIcon={<Download size={14} />}
                onClick={() => void download(att)}
              />
              {canUpload && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={t('attachments.edit')}
                    leftIcon={<Pencil size={14} />}
                    onClick={() => openEdit(att)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={t('attachments.delete')}
                    leftIcon={<Trash2 size={14} />}
                    onClick={() => setDeleting(att)}
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={anyModalOpen}
        onClose={() => !formBusy && closeModal()}
        title={editing ? t('attachments.editTitle') : t('attachments.modalTitle')}
        description={editing ? undefined : t('attachments.modalDesc')}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={formBusy}>
              {t('common.cancel')}
            </Button>
            {editing ? (
              <Button loading={updateMutation.isPending} onClick={submitEdit}>
                {t('common.save')}
              </Button>
            ) : (
              <Button
                leftIcon={<Paperclip size={14} />}
                disabled={!file}
                loading={uploadMutation.isPending}
                onClick={submit}
              >
                {t('attachments.upload')}
              </Button>
            )}
          </>
        }
      >
        <div className={styles.uploadForm}>
          <Input
            label={t('attachments.titleLabel')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('attachments.titlePlaceholder')}
            fullWidth
          />
          <TextArea
            label={t('attachments.descriptionLabel')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('attachments.descriptionPlaceholder')}
            rows={2}
            fullWidth
          />
          <Select
            label={t('attachments.kindLabel')}
            value={kind}
            onChange={(e) => setKind(e.target.value as AttachmentKind)}
            fullWidth
            options={(Object.keys(KIND_LABEL_KEY) as AttachmentKind[]).map((k) => ({
              value: k,
              label: t(KIND_LABEL_KEY[k]),
            }))}
          />
          {!editing && (
            <div
              className={styles.fileField}
              onDragOver={onFileFieldDragOver}
              onDrop={onFileFieldDrop}
            >
              <span className={styles.fileLabel} id="artifact-file-label">
                {t('attachments.fileLabel')}
              </span>
              <div className={styles.fileRow}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  leftIcon={<Paperclip size={14} />}
                  onClick={() => fileInputRef.current?.click()}
                  aria-labelledby="artifact-file-label"
                >
                  {t('attachments.chooseFile')}
                </Button>
                <span className={styles.fileName} data-testid="artifact-file-name">
                  {file ? file.name : t('attachments.noFileChosen')}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className={styles.fileInput}
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={deleting !== null}
        onClose={() => !deleteMutation.isPending && setDeleting(null)}
        title={t('attachments.deleteTitle')}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleting(null)}
              disabled={deleteMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => deleting && deleteMutation.mutate(deleting.id)}
            >
              {t('attachments.delete')}
            </Button>
          </>
        }
      >
        <p>
          {deleting &&
            t('attachments.deleteConfirm', { name: deleting.title || deleting.filename })}
        </p>
      </Modal>
    </section>
  );
}
